import { useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { ChevronRight, Users } from "lucide-react";

const JOB_FLOW = [
  "design_pending", "designing", "design_approval", "design_approved",
  "printing", "finishing", "quality_check", "packing", "ready",
  "out_for_delivery", "delivered", "completed",
];

const STATUS_LABELS = {
  design_pending: "Design Pending",
  designing: "Designing",
  design_approval: "Design Approval",
  design_approved: "Design Approved",
  printing: "Printing",
  finishing: "Finishing",
  quality_check: "Quality Check",
  packing: "Packing",
  ready: "Ready",
  out_for_delivery: "Out for Delivery",
  delivered: "Delivered",
  completed: "Completed",
  cancelled: "Cancelled",
};

const STATUS_COLORS = {
  design_pending: "#71717A",
  designing: "#0EA5E9",
  design_approval: "#EAB308",
  design_approved: "#22C55E",
  printing: "#FF6B00",
  finishing: "#F97316",
  quality_check: "#8B5CF6",
  packing: "#0EA5E9",
  ready: "#22C55E",
  out_for_delivery: "#3B82F6",
  delivered: "#16A34A",
  completed: "#059669",
  cancelled: "#EF4444",
};

function nextStatus(cur) {
  const idx = JOB_FLOW.indexOf(cur);
  return idx >= 0 && idx < JOB_FLOW.length - 1 ? JOB_FLOW[idx + 1] : null;
}

const CAN_UPDATE_JOB = ["admin", "super_admin", "office_staff", "production_manager", "machine_operator", "designer", "delivery_staff"];

export default function JobCard({ it, showAmount, orderId, role, onChanged }) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const canUpdate = CAN_UPDATE_JOB.includes(role);
  const nxt = nextStatus(it.status);
  const color = STATUS_COLORS[it.status] || "#71717A";

  const advance = async (target) => {
    setBusy(true); setErr("");
    try {
      await api.patch(`/orders/${orderId}/jobs/${it.job_id}/status`, {
        status: target,
        note: `${STATUS_LABELS[target]}`,
      });
      onChanged && onChanged();
    } catch (e) { setErr(formatApiError(e)); } finally { setBusy(false); }
  };

  return (
    <div className="border-2 border-[hsl(var(--border))] hover:border-[#FF6B00]/50 transition-colors p-4 space-y-3" data-testid={`job-card-${it.job_id}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-mono text-[10px] font-bold text-muted-foreground tracking-wider">{it.job_no || "JOB"}</span>
            <span
              className="inline-flex items-center gap-1.5 border px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider"
              style={{ borderColor: color, color }}
              data-testid={`job-status-${it.job_id}`}
            >
              <span className="w-1.5 h-1.5 rounded-full" style={{ background: color }} />
              {STATUS_LABELS[it.status] || it.status}
            </span>
            {it.priority && it.priority !== "normal" && (
              <span className="text-[10px] font-bold uppercase text-red-500 border border-red-500 px-1.5 py-0.5">
                {it.priority}
              </span>
            )}
          </div>
          <div className="font-semibold text-sm mt-1">{it.product_category}{it.product_name ? ` — ${it.product_name}` : ""}</div>
        </div>
        {showAmount && (
          <div className="font-mono text-sm font-bold whitespace-nowrap">₹{Math.round(it.total || 0).toLocaleString("en-IN")}</div>
        )}
      </div>

      {(it.sizes || []).length > 0 && (
        <table className="w-full text-xs">
          <thead>
            <tr className="text-muted-foreground">
              <th className="text-left">Size</th>
              <th className="text-right">Qty</th>
              <th className="text-right">Rate</th>
              <th className="text-right">Amount</th>
            </tr>
          </thead>
          <tbody>
            {it.sizes.map((s, si) => (
              <tr key={si}>
                <td>{(s.width && s.height) ? `${s.width}×${s.height} ${s.unit || "ft"}` : "—"}</td>
                <td className="text-right font-mono">{s.quantity}</td>
                <td className="text-right font-mono">₹{Math.round(s.rate || 0)}</td>
                <td className="text-right font-mono">₹{Math.round(s.amount || 0).toLocaleString("en-IN")}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {(it.addons || []).length > 0 && (
        <div className="text-xs text-muted-foreground">
          Add-ons: {it.addons.map((a) => `${a.name} × ${a.quantity} (₹${Math.round(a.amount || 0)})`).join(", ")}
        </div>
      )}

      {showAmount && Number(it.gst_rate || 0) > 0 && (
        <div className="text-xs text-muted-foreground">
          GST @ {it.gst_rate}%{it.gst_mode === "included" ? " (included)" : ""} = ₹{Math.round(it.tax_amount || 0).toLocaleString("en-IN")}
        </div>
      )}

      {canUpdate && (
        <div className="flex items-center gap-2 flex-wrap pt-2 border-t border-[hsl(var(--border))]">
          {nxt && (
            <button
              onClick={() => advance(nxt)}
              disabled={busy}
              className="inline-flex items-center gap-1 bg-[#FF6B00] text-white text-xs px-3 py-1.5 hover:bg-[#e65a00] disabled:opacity-50"
              data-testid={`advance-job-${it.job_id}`}
            >
              <ChevronRight size={12} /> {STATUS_LABELS[nxt]}
            </button>
          )}
          <select
            value={it.status}
            onChange={(e) => advance(e.target.value)}
            disabled={busy}
            className="text-xs border border-[hsl(var(--border))] bg-transparent px-2 py-1.5 outline-none"
            data-testid={`job-status-select-${it.job_id}`}
          >
            {JOB_FLOW.map((s) => (
              <option key={s} value={s}>{STATUS_LABELS[s]}</option>
            ))}
            <option value="cancelled">Cancelled</option>
          </select>
          {(it.timeline || []).length > 1 && (
            <span className="text-[10px] text-muted-foreground inline-flex items-center gap-1">
              <Users size={10} /> {it.timeline.length} updates
            </span>
          )}
        </div>
      )}
      {err && <div className="text-xs text-red-600">{err}</div>}
    </div>
  );
}
